import { parseSseFrame, type SseFrame } from '@d-orca/shared/sse';

/**
 * The frames of a `text/event-stream` response body, as they arrive. Every
 * stream the app reads with `fetch` (the assistant, equipment activity) goes
 * through this one loop.
 *
 * The `finally` is load-bearing, not tidiness: without `reader.cancel()` an
 * aborted stream leaves the body locked until GC runs, and repeated abort+retry
 * cycles then exhaust the per-origin connection pool. It runs however the
 * reading ends — the body done, a `break` or throw in the consumer's
 * `for await`, or the fetch aborted. `cancel()` swallows its own AbortError.
 */
export async function* sseFrames(body: ReadableStream<Uint8Array>): AsyncGenerator<SseFrame> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      // Normalise CRLF → LF so the frame splitter matches the spec-form
      // `\r\n\r\n` separator that some upstreams emit.
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const raw = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const frame = parseSseFrame(raw);
        if (frame) yield frame;
      }
    }
  } finally {
    try {
      await reader.cancel();
    } catch {
      // ignore
    }
  }
}
